import React from "react";
import Form from "../components/common/form";
import PlanCard from "../components/common/planCard";
import subscriptionService from "../services/subscriptionService";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@material-ui/core";
import qs from "query-string";
import { sortPlansByFee } from "../utils/planUtils";
import userService from "../services/userService";
import { toast } from "react-toastify";
import {
  getIndividualPurchaseConfirmationMessage,
  requiresIndividualPurchaseConfirmation,
} from "../utils/companyPurchaseUtils";
import { getPlanID, getPlanFee } from "../utils/planAccessors";

const activePlanColor = "#2cae6c";
const pendingPlanColor = "#ff7a00";
const paymentReconciliationIntervalSeconds = 5 * 60;

export class Subscriptions extends Form {
  state = {
    data: {
      plans: [],
    },
    lastPaymentStatus: "",
    lastPaymentID: "",
    planDisplayOrder: {},
    errors: {},
    currentTimestamp: Date.now(),
    loadInterval: null,
    countdownInterval: null,
    invoiceTaxCode: "0",
    individualPurchaseDialogOpen: false,
    paymentStatusCheckInProgress: false,
	loadInProgress: false,
  };

  individualPurchaseDialogResolve = null;

  componentDidMount = async () => {
    const lastPaymentStatus = this.getLastPaymentStatusFromReturn();
    const lastPaymentTransactionID = this.getLastPaymentTransactionIDFromReturn();
    if (lastPaymentStatus !== "") {
      try {
        await subscriptionService.reconcileLatestPayment(lastPaymentTransactionID);
      } catch (ex) {}
    }

	this.loadProfile().catch(() => {});
	this.loadData();
    let loadInterval = setInterval(this.loadData, 3000);
    let countdownInterval = setInterval(() => {
      this.setState({ currentTimestamp: Date.now() });
    }, 1000);
    this.setState({
      loadInterval: loadInterval,
      countdownInterval: countdownInterval,
    });
  };

  componentWillUnmount = () => {
    if (this.individualPurchaseDialogResolve) {
      this.individualPurchaseDialogResolve(false);
      this.individualPurchaseDialogResolve = null;
    }

    clearInterval(this.state.loadInterval);
    clearInterval(this.state.countdownInterval);
    this.setState({
      loadInterval: null,
      countdownInterval: null,
      individualPurchaseDialogOpen: false,
    });
  };

	stopPolling = () => {
	  clearInterval(this.state.loadInterval);
	  clearInterval(this.state.countdownInterval);
	  this.setState({ loadInterval: null, countdownInterval: null });
	};

  getLastPaymentStatusFromReturn = () => {
    const r = qs.parse(this.props.location.search).r;
    if (r !== undefined) {
      const parsed = JSON.parse(atob(r));
      return String(parsed["e"]).toLowerCase();
    }

    return "";
  };

  getLastPaymentTransactionIDFromReturn = () => {
    const r = qs.parse(this.props.location.search).r;
    if (r !== undefined) {
      const parsed = JSON.parse(atob(r));
      return String(parsed["t"] || "");
    }

    return "";
  };

  maybeReconcileReturnedPayment = async (
    lastPaymentStatus,
    lastPaymentTransactionID,
    subscriptions
  ) => {
    if (lastPaymentStatus === "" || lastPaymentTransactionID === "") {
      return false;
    }

    const hasMatchingPendingSubscription = subscriptions.some((subscription) => {
      return (
        subscription["processing_status"] == "pending" &&
        String(subscription["last_payment_transaction_id"] || "") ===
          lastPaymentTransactionID
      );
    });

    if (!hasMatchingPendingSubscription) {
      return false;
    }

    try {
      await subscriptionService.reconcileLatestPayment(lastPaymentTransactionID);
      return true;
    } catch (ex) {
      return false;
    }
  };

  getActivePlan = () => {
    return this.state.data.plans.find(
      (plan) => plan["ActiveSubscription"] !== undefined
    );
  };

  getPendingPlan = () => {
    return this.state.data.plans.find(
      (plan) => plan["PendingSubscription"] !== undefined
    );
  };

  hasOtherPendingPlan = (plan) => {
    const pendingPlan = this.getPendingPlan();
    return !!pendingPlan && getPlanID(pendingPlan) !== getPlanID(plan);
  };

  getBlockingPaidPlan = () => {
    return this.state.data.plans.find((plan) => {
      return (
        getPlanFee(plan) > 0 && plan["ActiveSubscription"] !== undefined
      );
    });
  };

  loadData = async () => {
	if (this.state.loadInProgress) return;
	this.setState({ loadInProgress: true });
	try {
    let data = { ...this.state.data };
    const plansResponse = await subscriptionService.listPlans();
    const plans = plansResponse.data && Array.isArray(plansResponse.data.plans)
      ? plansResponse.data.plans
      : [];
    const lastPaymentStatus = this.getLastPaymentStatusFromReturn();
    const lastPaymentTransactionID = this.getLastPaymentTransactionIDFromReturn();
    let subscriptionsResponse = await subscriptionService.listSubscriptions();
    let subscriptions = subscriptionsResponse.data.subscriptions;

    if (
      await this.maybeReconcileReturnedPayment(
        lastPaymentStatus,
        lastPaymentTransactionID,
        subscriptions
      )
    ) {
      subscriptionsResponse = await subscriptionService.listSubscriptions();
      subscriptions = subscriptionsResponse.data.subscriptions;
    }

    const latestPaymentTransactionID =
      subscriptionsResponse.data.latest_payment_transaction_id || "";
    const sortedPlansByFee = sortPlansByFee(plans);
    const planDisplayOrder = { ...this.state.planDisplayOrder };

    sortedPlansByFee.forEach((plan, index) => {
      const planID = getPlanID(plan);
      if (planDisplayOrder[planID] === undefined) {
        planDisplayOrder[planID] = index;
      }
    });

    data.plans = sortedPlansByFee
      .map((p) => {
        const plan = { ...p };
        const planID = getPlanID(plan);
        plan["ActiveSubscription"] = subscriptions.find(
          (s) => s["plan_id"] == planID && s["active"]
        );
        plan["PendingSubscription"] = subscriptions.find(
          (s) =>
            s["plan_id"] == planID &&
            !s["active"] &&
            s["processing_status"] == "pending"
        );
        return plan;
      });

    this.setState({
      data,
      lastPaymentStatus,
      lastPaymentID: lastPaymentStatus ? latestPaymentTransactionID : "",
      planDisplayOrder,
    });
	} catch (ex) {
	  if (ex.response && ex.response.status === 401) this.stopPolling();
	} finally {
	  this.setState({ loadInProgress: false });
	}
  };

  loadProfile = async () => {
    const userData = (await userService.getUserProfile()).data.user;
    this.setState({
      invoiceTaxCode: String(userData["InvoiceTaxCode"] || "0"),
    });
  };

  sortPlansByFee = (plans) => {
    return sortPlansByFee(plans, this.state.planDisplayOrder);
  };

  requestIndividualPurchaseConfirmation = () => {
    return new Promise((resolve) => {
      this.individualPurchaseDialogResolve = resolve;
      this.setState({ individualPurchaseDialogOpen: true });
    });
  };

  closeIndividualPurchaseDialog = (confirmed) => {
    const resolve = this.individualPurchaseDialogResolve;
    this.individualPurchaseDialogResolve = null;
    this.setState({ individualPurchaseDialogOpen: false }, () => {
      if (resolve) {
        resolve(confirmed);
      }
    });
  };

  getPendingCountdownLabel = (plan) => {
    const pendingSubscription = plan["PendingSubscription"];
    if (
      !pendingSubscription ||
      !pendingSubscription["pending_payment_created_at"]
    ) {
      return "05:00";
    }

    const pendingSinceMs = Date.parse(
      pendingSubscription["pending_payment_created_at"]
    );
    if (Number.isNaN(pendingSinceMs)) {
      return "05:00";
    }

    const elapsedSeconds = Math.max(
      0,
      Math.floor((this.state.currentTimestamp - pendingSinceMs) / 1000)
    );
    const remainingSeconds = Math.max(
      0,
      paymentReconciliationIntervalSeconds - elapsedSeconds
    );
    const minutes = String(Math.floor(remainingSeconds / 60)).padStart(2, "0");
    const seconds = String(remainingSeconds % 60).padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  shouldShowReturnedPaymentCheck = (plan) => {
    return (
      !!plan["PendingSubscription"] &&
      this.state.lastPaymentStatus === "success" &&
      this.state.lastPaymentID !== ""
    );
  };

  checkReturnedPaymentStatus = async () => {
    this.setState({ paymentStatusCheckInProgress: true });

    try {
      await subscriptionService.reconcileLatestPayment(this.state.lastPaymentID);
      await this.loadData();
    } catch (ex) {
      toast.error("A fizetés állapotának ellenőrzése nem sikerült.");
    } finally {
      this.setState({ paymentStatusCheckInProgress: false });
    }
  };

  renderLastPaymentStatus = (status, transactionID) => {
    return (
      <Card style={{ marginBottom: "10px" }}>
        <CardContent>
          <span style={{ color: "#666666" }}>Az fizetés eredménye:</span>
          <div style={{ fontSize: "18px", fontWeight: "bold" }}>
            {status == "success" && (
              <div>
                Sikeres tranzakció.
                <br />
                <br />
                SimplePay tranzakció azonosító: {transactionID}
              </div>
            )}
            {status == "fail" && (
              <div>
                Sikertelen tranzakció
                <br />
                <br />
                SimplePay tranzakció azonosító: {transactionID} <br />
                Kérjük, ellenőrizze a tranzakció során megadott adatok
                helyességét. Amennyiben minden adatot helyesen adott meg, a
                visszautasítás okának kivizsgálása érdekében kérjük,
                szíveskedjen kapcsolatba lépni kártyakibocsátó bankjával.
              </div>
            )}
            {status == "timeout" && (
              <div>
                Ön túllépte a tranzakció elindításának lehetséges maximális
                idejét
              </div>
            )}
            {status == "cancel" && <div>Ön megszakította a fizetést</div>}
          </div>
        </CardContent>
      </Card>
    );
  };

  renderPlan = (key, plan) => {
    const isActivePlan = plan["ActiveSubscription"] != undefined;

    return (
      <div key={key} style={{ marginBottom: "10px" }}>
        <PlanCard
          plan={plan}
          cardStyle={
            isActivePlan
              ? {
                  border: `2px solid ${activePlanColor}`,
                  boxShadow: `0 0 0 3px rgba(44, 174, 108, 0.12)`,
                }
              : {}
          }
        >
          {plan["ActiveSubscription"] != undefined
            ? this.renderPlanStatus(plan)
            : this.renderPlanSubscribe(plan)}
        </PlanCard>
      </div>
    );
  };

  renderPlanStatus = (plan) => {
    const isPaidPlan = getPlanFee(plan) > 0;
    const isTerminating = plan["ActiveSubscription"]["status"] === "pending_cancellation";

    return (
      <div>
        Az előfizetés aktív! Fizetve eddig: {plan["ActiveSubscription"]["expire_at"]}{" "}
        <br />
        <br />
        <Button
          variant="contained"
          disabled
          style={{
            backgroundColor: activePlanColor,
            color: "#ffffff",
            opacity: 1,
            cursor: "default",
          }}
        >
          Aktív előfizetés
        </Button>
        <br />
        <br />
        {!isPaidPlan ? null : isTerminating ? (
          <Typography component="div" style={{ color: "#666666" }}>
            Az automatikus megújítás lemondva. A csomag automatikusan lejár ekkor:{" "}
            {plan["ActiveSubscription"]["expire_at"]}. Ezután az ingyenes csomag lesz aktív.
          </Typography>
        ) : (
          <Button
            color="secondary"
            onClick={() => {
              if (
                window.confirm(
                  "Biztosan megszakítod előfizetésed automatikus megújítását?"
                )
              ) {
                this.cancelPlan(plan);
              }
            }}
          >
            A szolgáltatás lemondása
          </Button>
        )}
      </div>
    );
  };

  renderPlanSubscribe = (plan) => {
    if (plan["PendingSubscription"]) {
      const pendingCountdownLabel = this.getPendingCountdownLabel(plan);
      const shouldShowReturnedPaymentCheck = this.shouldShowReturnedPaymentCheck(plan);

      return (
        <div
          style={{
            width: "100%",
            textAlign: "center",
            fontSize: "20px",
            fontWeight: "bold",
            color: pendingPlanColor,
            backgroundColor: "#fff1e6",
            border: `2px solid ${pendingPlanColor}`,
            borderRadius: "6px",
            padding: "12px 16px",
            boxShadow: "0 0 0 3px rgba(255, 122, 0, 0.12)",
          }}
        >
          A fizetés feldolgozása folyamatban!
          <div
            style={{
              fontSize: "16px",
              fontWeight: "normal",
              marginTop: "8px",
            }}
          >
            Várható legfeljebb: {pendingCountdownLabel}
          </div>
          <Button
            variant="outlined"
            style={{
              marginTop: "14px",
              borderColor: pendingPlanColor,
              color: pendingPlanColor,
              fontWeight: "bold",
            }}
            disabled={this.state.paymentStatusCheckInProgress}
            onClick={() => {
              if (shouldShowReturnedPaymentCheck) {
                this.checkReturnedPaymentStatus();
                return;
              }

              this.cancelPlan({ ActiveSubscription: plan["PendingSubscription"] });
            }}
          >
            {shouldShowReturnedPaymentCheck
              ? "Fizetés státuszának ellenőrzése"
              : "Fizetés megszakítása"}
          </Button>
        </div>
      );
    }

    const blockingPaidPlan = this.getBlockingPaidPlan();
    if (blockingPaidPlan) {
      const blockingSubscription = blockingPaidPlan["ActiveSubscription"];
      const expiryLabel = blockingSubscription
        ? blockingSubscription["expire_at"]
        : "-";
      const isTerminating =
        blockingSubscription &&
        blockingSubscription["status"] === "pending_cancellation";

      return (
        <Typography component="div" style={{ color: "#666666" }}>
          {isTerminating
            ? `Az új csomagok a jelenlegi előfizetés ${expiryLabel} napján történő lejárata után lesznek elérhetőek.`
            : `A jelenlegi fizetős előfizetésed ${expiryLabel} napjáig aktív, ezért most nem válthatsz csomagot.`}
        </Typography>
      );
    }

    const disablePlanActions = this.hasOtherPendingPlan(plan);

    return (
      <div>
        <Button
          variant="contained"
          color="primary"
          disabled={disablePlanActions}
          onClick={() => this.subscribeToPlan(plan)}
        >
          Előfizetek
        </Button>
      </div>
    );
  };

  cancelPlan = async (plan) => {
    await subscriptionService.cancel(plan["ActiveSubscription"]["id"]);
    this.loadData();
  };

  subscribeToPlan = async (plan) => {
    const needsIndividualPurchaseConfirmation = requiresIndividualPurchaseConfirmation(
      plan,
      this.state.invoiceTaxCode
    );
    const confirmIndividualPurchase = needsIndividualPurchaseConfirmation
      ? await this.requestIndividualPurchaseConfirmation()
      : false;

    if (needsIndividualPurchaseConfirmation && !confirmIndividualPurchase) {
      return;
    }

    let resp;
    try {
      resp = await subscriptionService.subscribe(plan, {
        confirmIndividualPurchase,
      });
    } catch (ex) {
      if (ex.response && ex.response.data && ex.response.data.error) {
        toast.error(ex.response.data.error);
        return;
      }

      throw ex;
    }

    if (resp.data.payment_required === false || !resp.data.payment_url) {
      this.loadData();
      return;
    }

    document.location = resp.data.payment_url;
  };

  render() {
    const sortedPlans = this.sortPlansByFee(this.state.data.plans);

    return (
      <div>
        <h1>Előfizetés</h1>

        {this.state.lastPaymentStatus != ""
          ? this.renderLastPaymentStatus(
              this.state.lastPaymentStatus,
              this.state.lastPaymentID
            )
          : null}

        {sortedPlans.map((plan) => this.renderPlan(getPlanID(plan), plan))}

        <Dialog
          open={this.state.individualPurchaseDialogOpen}
          onClose={() => this.closeIndividualPurchaseDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Magánszemély előfizetés megerősítése</DialogTitle>
          <DialogContent>
            <DialogContentText
              component="div"
              style={{
                color: "#2d2d2d",
                lineHeight: 1.6,
                whiteSpace: "pre-line",
              }}
            >
              {getIndividualPurchaseConfirmationMessage()}
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: "8px 24px 20px" }}>
            <Button onClick={() => this.closeIndividualPurchaseDialog(false)}>
              Mégsem
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => this.closeIndividualPurchaseDialog(true)}
            >
              Elfogadom
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

export default Subscriptions;
